// Compound BEGIN…END block awareness for the document scanner.
//
// Lets a CREATE/ALTER PROCEDURE|FUNCTION|TRIGGER|EVENT body (or an Oracle
// anonymous PL/SQL block) keep its `;`-terminated inner statements as ONE
// scanned statement, instead of the scanner splitting at the first inner `;`.
//
// SAFETY — the change is doubly gated so it cannot regress ordinary SQL:
//   • Context gate: block tracking only activates once the *current* statement
//     is classified as a routine/trigger body. Plain DML, transaction-control
//     `BEGIN; … COMMIT;`, and `IF(...)` / `CASE…END` expressions outside a
//     routine body are never affected (they are not classified as routine DDL,
//     so the `;`-suppression logic never engages).
//   • Engine gate: Postgres is excluded entirely — its routine bodies are
//     dollar-quoted, which the scanner already protects; bare `BEGIN/COMMIT`
//     stay separate transaction statements.
//
// Only the language-service document scanner consumes this. Query *execution*
// statement splitting is a separate, independent splitter
// (electron/query-engine-adapters/splitSqlStatements.ts) and is unaffected.

import type { ParseOptions } from '../model';

type TrackedEngine = 'mssql' | 'mysql' | 'oracle' | 'sqlite';

// Keywords that open a compound block and pair with a closing `END`. Engine-
// specific because the same word is not always a block opener: T-SQL `IF`/
// `WHILE` take a single statement or a `BEGIN…END` block (no `END IF`), and
// Oracle `WHILE`/`FOR` are followed by `LOOP` (the `LOOP` is the block, not the
// `WHILE`).
const OPENERS: Record<TrackedEngine, ReadonlySet<string>> = {
  mysql: new Set(['BEGIN', 'CASE', 'IF', 'LOOP', 'WHILE', 'REPEAT']),
  oracle: new Set(['BEGIN', 'CASE', 'IF', 'LOOP']),
  mssql: new Set(['BEGIN', 'CASE']),
  sqlite: new Set(['BEGIN', 'CASE']),
};

// Words that may immediately follow a closing `END` (e.g. `END IF`, `END LOOP`,
// `END TRY`). Swallowed so they are not re-counted as openers.
const END_KINDS: ReadonlySet<string> = new Set([
  'IF', 'LOOP', 'WHILE', 'REPEAT', 'CASE', 'TRY', 'CATCH',
]);

// Object kinds whose CREATE/ALTER carries a procedural body.
const ROUTINE_KINDS: ReadonlySet<string> = new Set([
  'PROCEDURE', 'FUNCTION', 'TRIGGER', 'EVENT', 'PACKAGE', 'TYPE',
]);

// Lead-word noise tolerated between CREATE/ALTER and the object kind while still
// scanning for classification (e.g. `CREATE OR REPLACE …`, `CREATE DEFINER=…`).
// `ALTER` covers T-SQL's `CREATE OR ALTER PROCEDURE|FUNCTION|TRIGGER` — without
// it the scan stopped at `ALTER`, never classified the routine body, and the
// body's inner `;` fragmented the statement (WS-E regression, 2026-07-12). As a
// FIRST word `ALTER` still takes the CREATE/ALTER branch in classifyLeadWord;
// inside any other head (`ALTER TABLE …`) the following word (`TABLE`) is not a
// ROUTINE_KIND, so classification still lands on "plain" exactly as before.
const LEAD_NOISE: ReadonlySet<string> = new Set([
  'OR', 'ALTER', 'REPLACE', 'DEFINER', 'ALGORITHM', 'SQL', 'SECURITY', 'NONCLUSTERED',
]);

export interface BlockState {
  /** Current statement classified as a routine/trigger body (or anon block). */
  isRoutineBody: boolean;
  /** Classification decided — stop inspecting lead words. */
  classified: boolean;
  /** First significant word of the current statement. */
  firstWord: string | null;
  /** BEGIN/CASE/IF/LOOP/… nesting depth within the routine body. */
  depth: number;
  /** The previous word was `END` — swallow a trailing kind-word if present. */
  swallowKind: boolean;
}

export function newBlockState(): BlockState {
  return { isRoutineBody: false, classified: false, firstWord: null, depth: 0, swallowKind: false };
}

/** Reset at every statement boundary (the scanner emits a statement). */
export function resetBlockState(st: BlockState): void {
  st.isRoutineBody = false;
  st.classified = false;
  st.firstWord = null;
  st.depth = 0;
  st.swallowKind = false;
}

export function blockTrackingEngine(options: ParseOptions): TrackedEngine | null {
  // Opt-in only — every consumer that does not set `compoundBlockAware` keeps
  // the universal split-on-`;` behaviour (the procedural reference indexer, the
  // normalizer, intellisense, …). Postgres is never tracked (dollar-quoted).
  if (!options.compoundBlockAware) return null;
  const e = options.engine;
  return e === 'mssql' || e === 'mysql' || e === 'oracle' || e === 'sqlite' ? e : null;
}

/**
 * Feed one identifier/keyword token (already extracted by the scanner) to the
 * block tracker. Mutates `st` — classification + depth bookkeeping.
 *
 * `treatBareBeginAsRoutineBody` is the opt-in, ADDITIVE flag documented on
 * `ParseOptions.treatBareBeginAsRoutineBody` — OFF (`false`) reproduces
 * today's behaviour byte-for-byte; the VQE is the only caller that turns it
 * on. `document/splitForExecution.ts` never sets it, so live query execution
 * batching is unaffected regardless of this flag's value.
 */
export function feedBlockWord(
  st: BlockState,
  rawWord: string,
  engine: TrackedEngine,
  treatBareBeginAsRoutineBody = false,
): void {
  const word = rawWord.toUpperCase();

  // `END <kind>` — swallow the kind-word so it is not re-counted as an opener.
  if (st.swallowKind) {
    st.swallowKind = false;
    if (END_KINDS.has(word)) return;
  }

  if (!st.classified) classifyLeadWord(st, word, engine, treatBareBeginAsRoutineBody);

  if (!st.isRoutineBody) return;

  if (word === 'END') {
    if (st.depth > 0) st.depth--;
    st.swallowKind = true;
  } else if (OPENERS[engine].has(word)) {
    st.depth++;
  }
}

function classifyLeadWord(
  st: BlockState,
  word: string,
  engine: TrackedEngine,
  treatBareBeginAsRoutineBody: boolean,
): void {
  if (st.firstWord === null) {
    st.firstWord = word;
    // Oracle anonymous PL/SQL block — `DECLARE …` or a bare `BEGIN … END;`.
    // Unconditional (not gated by the opt-in below) — this has shipped for
    // Oracle regardless of the flag since before it existed.
    if (engine === 'oracle' && (word === 'DECLARE' || word === 'BEGIN')) {
      st.isRoutineBody = true;
      st.classified = true;
      return;
    }
    // Opt-in (VQE statement classification only, see ParseOptions
    // .treatBareBeginAsRoutineBody): a bare `BEGIN` — no CREATE/ALTER wrapper
    // — as the first word of the statement on mssql/mysql/sqlite. Lets a
    // user-authored standalone `BEGIN … END` batch (T-SQL control-of-flow
    // block, MySQL/SQLite trigger-style body pasted without its CREATE
    // wrapper) collapse to one statement instead of fragmenting at every
    // inner `;`, mirroring the CREATE/ALTER routine-body case below. KNOWN
    // TRADE-OFF: this also swallows a `BEGIN TRANSACTION …`/`BEGIN; …` script
    // that never closes with a matching `END` — depth never returns to 0, so
    // the rest of the buffer runs to EOF as one statement. Accepted because
    // (a) the VQE classifies one tab's buffer, not a multi-statement script,
    // and (b) it mirrors the pre-existing "malformed/unterminated routine
    // body swallows to EOF" behaviour `compoundBlockAware` already has for
    // CREATE/ALTER bodies missing their closing END. OFF by default; never
    // set by document/splitForExecution.ts.
    if (treatBareBeginAsRoutineBody && engine !== 'oracle' && word === 'BEGIN') {
      st.isRoutineBody = true;
      st.classified = true;
      return;
    }
    // Only CREATE/ALTER can introduce a routine body; anything else is plain.
    if (word !== 'CREATE' && word !== 'ALTER') st.classified = true;
    return;
  }
  // Subsequent lead word after CREATE/ALTER.
  if (ROUTINE_KINDS.has(word)) {
    st.isRoutineBody = true;
    st.classified = true;
    return;
  }
  if (LEAD_NOISE.has(word)) return; // keep scanning lead words
  // A concrete non-routine object kind (TABLE, VIEW, INDEX, …) or any other
  // token — this CREATE/ALTER is not a routine body.
  st.classified = true;
}

/** Whether a delimiter (`;`) at the current position must NOT split. */
export function shouldSuppressSplit(st: BlockState): boolean {
  return st.isRoutineBody && st.depth > 0;
}
